import { imageUrl, type MediaContext } from "@/features/media/mediaUrl";
import { iconNameFromFile } from "@/features/map/utils/images";
import type {
  MapIcon,
  SecondaryLayer,
  SecondaryLayerSection,
  StyleLayer,
} from "@/types";

/** The layer types a swatch can stand for; anything else has nothing to draw. */
const DRAWABLE = new Set(["fill", "line", "circle", "symbol"]);

/** Shown when a layer's colour can't be determined at all. */
export const FALLBACK_COLOR = "#888888";

const COLOR_PREFIXES = ["#", "rgb", "rgba", "hsl", "hsla"];

function isColor(value: unknown): value is string {
  return (
    typeof value === "string" &&
    COLOR_PREFIXES.some((prefix) => value.startsWith(prefix))
  );
}

/**
 * The colours hidden in one paint value.
 *
 * Paint properties come in three shapes: a plain colour, a Mapbox expression
 * whose literal arms are colours, and the older `{ stops: [[input, colour]] }`
 * form. All three appear in tenant styles, so all three are read.
 */
export function paintColors(value: unknown): string[] {
  if (!value) return [];
  if (isColor(value)) return [value];

  if (Array.isArray(value)) return value.filter(isColor);

  if (typeof value === "object") {
    const stops = (value as { stops?: unknown }).stops;
    if (Array.isArray(stops)) {
      return stops
        .map((stop) => (Array.isArray(stop) ? stop[1] : null))
        .filter(isColor);
    }
  }

  return [];
}

/** The colours a style layer paints with — the first paint key that yields any. */
export function layerColors(paint: Record<string, unknown> | undefined): string[] {
  const candidates = [
    paint?.["fill-color"],
    paint?.["line-color"],
    paint?.["circle-color"],
    paint?.["text-color"],
  ];

  for (const candidate of candidates) {
    const colors = [...new Set(paintColors(candidate))];
    if (colors.length > 0) return colors;
  }

  return [FALLBACK_COLOR];
}

/**
 * What makes two style layers look the same in a legend.
 *
 * Tenant styles routinely carry a layer several times over — once per zoom band,
 * once per source — and drawing a swatch for each would repeat the same mark
 * across the row.
 */
export function swatchSignature(layer: StyleLayer): string {
  return JSON.stringify({
    type: layer.type ?? "unknown",
    colors: layerColors(layer.paint).slice().sort(),
    icon: layer.layout?.["icon-image"] ?? "",
    hasText: !!layer.layout?.["text-field"],
  });
}

/**
 * The style layers of one secondary layer that a legend can speak for.
 *
 * A layer whose paint is data-driven — any value that is an expression array — is
 * skipped: its colour depends on the feature, so a single swatch would claim
 * something untrue about the rest.
 */
export function swatchLayers(layer: SecondaryLayer): StyleLayer[] {
  const seen = new Set<string>();

  return (layer.layers ?? []).filter((styleLayer) => {
    if (!DRAWABLE.has(styleLayer.type ?? "")) return false;
    if (Object.values(styleLayer.paint ?? {}).some(Array.isArray)) return false;

    const signature = swatchSignature(styleLayer);
    if (seen.has(signature)) return false;

    seen.add(signature);
    return true;
  });
}

export interface LegendSection {
  section: SecondaryLayerSection;
  layers: { layer: SecondaryLayer; styleLayers: StyleLayer[] }[];
}

/**
 * The legend's dynamic half: the layers that are switched on right now, grouped
 * by the section they belong to.
 *
 * Sections with nothing visible are dropped rather than shown empty — the legend
 * describes what is on the map, so a heading over nothing is a lie about it.
 */
export function visibleLegendSections(
  sections: SecondaryLayerSection[] | undefined,
  isActive: (name: string | undefined) => boolean
): LegendSection[] {
  return (sections ?? [])
    .map((section) => ({
      section,
      layers: (section.secondaryLayers ?? [])
        .filter((layer) => isActive(layer.name))
        .map((layer) => ({ layer, styleLayers: swatchLayers(layer) }))
        .filter((entry) => entry.styleLayers.length > 0),
    }))
    .filter((entry) => entry.layers.length > 0);
}

/**
 * The URL of a marker icon, by the id a style layer references it under.
 *
 * Resolved through the project's own icons rather than guessed from the id:
 * legacy built a path from the name and tried `.png`, then `.jpg`, then `.webp`
 * as each 404'd, because it had no mapping to hand. This takes the same list the
 * sprite is loaded from, which is why it must be the *collected* one — most
 * projects keep their artwork on `iconMapping`, not in a top-level `mapIcons`.
 */
export function mapIconUrl(
  iconId: unknown,
  mapIcons: MapIcon[] | undefined,
  media: MediaContext
): string | null {
  if (typeof iconId !== "string" || !iconId) return null;

  const icon = (mapIcons ?? []).find(
    (candidate) => candidate.name && iconNameFromFile(candidate.name) === iconId
  );

  return imageUrl(icon?.image?.filePath, "32x32", media);
}
